import { Voucher, Product, UserVoucher } from "../types";
import { getProductPrice } from "./productUtils";

/**
 * Cache for voucher-to-product matching results
 * Cache key format: `${productId}:${voucherIdsHash}`
 * Max size: 1000 entries (simple FIFO eviction)
 */
class VoucherMatchingCache {
  private cache = new Map<string, boolean>();
  private readonly maxSize = 1000;
  private accessOrder: string[] = [];

  private getCacheKey(productId: string, voucherIds: string[]): string {
    // Create a simple hash from voucher IDs
    const voucherIdsHash = voucherIds.sort().join(",");
    return `${productId}:${voucherIdsHash}`;
  }

  get(productId: string, voucherIds: string[]): boolean | undefined {
    const key = this.getCacheKey(productId, voucherIds);
    const result = this.cache.get(key);
    
    // Update access order (move to end)
    if (result !== undefined) {
      const index = this.accessOrder.indexOf(key);
      if (index > -1) {
        this.accessOrder.splice(index, 1);
      }
      this.accessOrder.push(key);
    }
    
    return result;
  }

  set(productId: string, voucherIds: string[], value: boolean): void {
    const key = this.getCacheKey(productId, voucherIds);
    
    // Evict oldest entry if cache is full
    if (this.cache.size >= this.maxSize && !this.cache.has(key)) {
      const oldestKey = this.accessOrder.shift();
      if (oldestKey) {
        this.cache.delete(oldestKey);
      }
    }
    
    this.cache.set(key, value);
    this.accessOrder.push(key);
    
    // Keep access order in sync
    if (this.accessOrder.length > this.maxSize) {
      this.accessOrder = this.accessOrder.slice(-this.maxSize);
    }
  }

  clear(): void {
    this.cache.clear();
    this.accessOrder = [];
  }
}

// Global cache instance
const matchingCache = new VoucherMatchingCache();

/**
 * Checks if any voucher from a list matches a product
 * Uses memoized cache for performance
 * @param vouchers - Array of user vouchers to check
 * @param product - The product to check against
 * @returns true if any voucher applies to the product
 */
export function vouchersMatchProduct(
  vouchers: UserVoucher[],
  product: Product
): boolean {
  if (!vouchers || vouchers.length === 0 || !product) {
    return false;
  }

  // Create cache key from voucher IDs
  const voucherIds = vouchers.map((uv) => uv.voucher.id || "").filter(Boolean);
  if (voucherIds.length === 0) {
    return false;
  }

  // Check cache first
  const cached = matchingCache.get(product.id, voucherIds);
  if (cached !== undefined) {
    return cached;
  }

  // Calculate matching result
  const result = vouchers.some((userVoucher) =>
    voucherMatchesProduct(userVoucher.voucher, product)
  );

  // Store in cache
  matchingCache.set(product.id, voucherIds, result);

  return result;
}

/**
 * Clears the voucher matching cache
 * Call this when vouchers are updated
 */
export function clearVoucherMatchingCache(): void {
  matchingCache.clear();
}

/**
 * Checks if a voucher matches a product based on voucher specifications
 * @param voucher - The voucher to check
 * @param product - The product to check against
 * @returns true if the voucher applies to the product
 */
export function voucherMatchesProduct(voucher: Voucher, product: Product): boolean {
  if (!voucher || !product) {
    return false;
  }

  // Must be free delivery type
  if (voucher.discountType !== "free_delivery") {
    return false;
  }

  // Check if voucher is global (applies to all products)
  if (voucher.isGlobal === true) {
    // Still need to check minimum purchase amount if specified
    if (voucher.minPurchaseAmount) {
      const productPrice = getProductPrice(product);
      return productPrice >= voucher.minPurchaseAmount;
    }
    return true;
  }

  // Check applicable products
  if (voucher.applicableProducts && voucher.applicableProducts.length > 0) {
    // If array contains '*', it applies to all products
    if (voucher.applicableProducts.includes("*")) {
      // Still need to check minimum purchase amount if specified
      if (voucher.minPurchaseAmount) {
        const productPrice = getProductPrice(product);
        return productPrice >= voucher.minPurchaseAmount;
      }
      return true;
    }

    // Check if product ID is in the applicable products array
    if (voucher.applicableProducts.includes(product.id)) {
      // Check minimum purchase amount if specified
      if (voucher.minPurchaseAmount) {
        const productPrice = getProductPrice(product);
        return productPrice >= voucher.minPurchaseAmount;
      }
      return true;
    }
  }

  // Check seller-specific vouchers
  if (voucher.sellerId) {
    if (product.sellerId === voucher.sellerId) {
      // Check minimum purchase amount if specified
      if (voucher.minPurchaseAmount) {
        const productPrice = getProductPrice(product);
        return productPrice >= voucher.minPurchaseAmount;
      }
      return true;
    }
    // If voucher is seller-specific but doesn't match, return false
    return false;
  }

  // If no specific matching criteria (no isGlobal, no applicableProducts, no sellerId)
  // Treat as global voucher (applies to all products) if it's a free delivery voucher
  // This handles vouchers that don't have explicit restrictions
  if (voucher.discountType === "free_delivery") {
    // Check minimum purchase amount if specified
    if (voucher.minPurchaseAmount) {
      const productPrice = getProductPrice(product);
      return productPrice >= voucher.minPurchaseAmount;
    }
    return true;
  }

  // If no specific matching criteria and not free delivery, return false
  return false;
}

